import React, { Component } from "react";
import PropTypes from "prop-types";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Form,
  FormGroup,
  Label,
  CardFooter,
  Input,
  Collapse,
  Spinner,
} from "reactstrap";
import { withRouter } from "react-router";
import moment from "moment";
import swal from "sweetalert2";
import Photo from "@/views/Components/Photo";
import Video from "@/views/Components/Video";

import { dateFormatter } from "@/util/formatter";
import { getStoredItem, setStoredItem } from "@/store/dataStore";
import { LIMITS_STORAGE_NAME, IS_TOURIST_PG } from "@/store/constants";
import limitsApi from "@/api/limits";
import verificationApi from "@/api/verification";
import usersApi from "@/api/users";

import { handleError } from "@/util/errorHandler";
import KeyRecoveryModal from "@/views/Components/KeyRecoveryRequest";
import GenerateQrModal from "@/views/Components/QrCode/GenerateQrModal";
import UpdateAccountModal from "@/views/Components/UpdateAccount/UpdateAccountModal";
import UpdateAdditionalInfoModal from "@/views/Components/UpdateAdditionalInfo/UpdateAdditionalInfoModal";
import PrepareRecoveryModal from "@/views/Components/PrepareRecoveryModal/PrepareRecoveryModal";
import TableRow from "../Components/DnDTable/TableRow";
import TableHeaderCell from "../Components/DnDTable/TableHeaderCell";
import ChangePhoneModal from "@/views/Components/ChangePhoneModal";
import { numberFormatter } from "@/util/formatter";

const GDI_STATUS_VIEW = {
  APPROVED: { icon: "fa-check-circle", color: "text-success" },
  IN_REVIEW: { icon: "fa-clock-o", color: "text-warning" },
};

// A document stays valid through its expiry day.
const isDocumentExpired = expiry =>
  Array.isArray(expiry) &&
  moment(expiry.join("/"), "YYYY/MM/DD").isBefore(moment(), "day");

class CustomerInfoCom extends Component {
  static propTypes = {
    data: PropTypes.object,
    onApprove: PropTypes.func,
    onReject: PropTypes.func,
    updateAccount: PropTypes.func,
    approvable: PropTypes.bool,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
  };

  static defaultProps = {
    data: {},
    onApprove: () => {},
    onReject: () => {},
    updateAccount: () => {},
    approvable: false,
  };

  constructor(props) {
    super(props);

    // Using localStorage instead of global state manager 😄
    const limitsList = getStoredItem(LIMITS_STORAGE_NAME);

    this.state = {
      limitsList: limitsList ? JSON.parse(limitsList) : [],
      selectedUserLimit: undefined,
      isDocsVisible: false,
      isDocsLoading: false,
      docs: "",
      photo: "",
      liveness: "",
      livenessState: "loading",
      isGdiVerifying: false,
      gdiResult: null,
      isPrepareRecoveryOpen: false,
      isKeyRecoveryOpen: false,
      isGenerateQrModalOpen: false,
      isUpdateAccountModalOpen: false,
      isChangePhoneModalOpen: false,
      isUpdateAdditionalInfoModalOpen: false,
      updateHistory: [],
      identityHistory: [],
    };
  }

  componentDidMount() {
    if (this.props.type !== "auditor") {
      this.loadOrTakeFromCacheLimitsList();
    }
    this.loadPhotos();
    this.loadLivenessVideo();
  }

  componentWillUnmount() {
    if (this.state.liveness) {
      URL.revokeObjectURL(this.state.liveness);
    }
  }

  componentDidUpdate(prevProps) {
    const { kycLimitId, identityUuid, photoUuid, accountId } = this.props.data;
    const isAuditor = this.props.type === "auditor";

    // Updating selected user limit because props updates asynchronously by api from parent component
    // Just payment for bad design of whole app
    if (prevProps.data.kycLimitId !== kycLimitId) {
      this.setState({ selectedUserLimit: kycLimitId });
    }

    if (
      prevProps.data.identityUuid !== identityUuid ||
      prevProps.data.photoUuid !== photoUuid
    ) {
      this.loadPhotos();
    }

    if (prevProps.data.accountId !== accountId && !isAuditor) {
      this.loadUserHistory(accountId);
      this.loadUserVerificationHistory(accountId);
    }

    if (prevProps.data.accountId !== accountId) {
      this.loadLivenessVideo();
      this.setState({ isGdiVerifying: false, gdiResult: null });
    }
  }

  onVerifyWithGdi = async () => {
    const { accountId } = this.props.data;

    this.setState({ isGdiVerifying: true, gdiResult: null });

    let gdiResult;
    try {
      const { status } =
        (await verificationApi.verifyPassportWithGdi(accountId)) || {};
      gdiResult = GDI_STATUS_VIEW[status] ? { status } : { error: "" };
    } catch (error) {
      gdiResult = { error: error.detail || error.message };
    }

    // Drop the response if the user navigated to another account meanwhile.
    if (this.props.data.accountId !== accountId) return;

    this.setState({ isGdiVerifying: false, gdiResult });
  };

  renderGdiResult = () => {
    const { gdiResult } = this.state;

    if (!gdiResult) return null;

    if (gdiResult.status) {
      const { icon, color } = GDI_STATUS_VIEW[gdiResult.status];
      return (
        <span className={`ml-3 font-weight-bold ${color}`}>
          <i className={`fa ${icon} mr-1`} />
          {gdiResult.status}
        </span>
      );
    }

    return (
      <span className="ml-3 text-danger">
        <i className="fa fa-exclamation-triangle mr-1" />
        <strong>ERROR:</strong>{" "}
        {gdiResult.error || "An unexpected error occurred"}
      </span>
    );
  };

  onLimitChange = ({ target: { value } }) => {
    this.setState({ selectedUserLimit: value });
  };

  getStatusIndicator = status => {
    const { selectedUserLimit, limitsList } = this.state;

    const {
      data: { accountType },
    } = this.props;

    const limitsOptionsForSelectedUserType = limitsList
      .filter(l => l.accountType === accountType)
      .map(l => (
        <option key={l.id} value={l.id}>
          {l.limitName}
        </option>
      ));

    return (
      <Row className="pb-5">
        <Col xs="10" sm="10" md={{ size: 6, offset: 3 }}>
          <Input
            disabled={status === "DELETED"}
            type="select"
            value={selectedUserLimit}
            onChange={this.onLimitChange}>
            {limitsOptionsForSelectedUserType}
          </Input>
        </Col>
        <Col xs="2" sm="2" md="3">
          {this.state.selectedUserLimit &&
            this.props.data.kycLimitId &&
            this.props.data.kycLimitId.toString() !==
              this.state.selectedUserLimit.toString() &&
            !this.props.approvable && (
              <Button
                color="success"
                type="button"
                onClick={this.onChangeUserLimit}>
                Save
              </Button>
            )}
        </Col>
      </Row>
    );
  };

  getFooterView = () => {
    const { onApprove, onReject, approvable } = this.props;
    const { selectedUserLimit } = this.state;

    if (approvable) {
      return (
        <CardFooter className="d-flex justify-content-center">
          <Button
            color="danger"
            type="button"
            className="mr-4"
            onClick={onReject}>
            REJECT
          </Button>
          <Button
            color="success"
            type="button"
            onClick={() => onApprove(selectedUserLimit)}>
            APPROVE
          </Button>
        </CardFooter>
      );
    }
  };

  loadOrTakeFromCacheLimitsList = () => {
    limitsApi
      .getLimitsListAll()
      .then(limitsList => {
        setStoredItem(LIMITS_STORAGE_NAME, JSON.stringify(limitsList));
        this.setState({ limitsList });
      })
      .catch(err => handleError(err));
  };

  loadPhotos = async () => {
    const { identityUuid, photoUuid } = this.props.data || {};

    if (identityUuid || photoUuid) {
      try {
        this.setState({ isDocsLoading: true });

        await Promise.all([
          this.loadIdentityPhoto(identityUuid),
          this.loadUserPhoto(photoUuid),
        ]);
      } catch (err) {
        console.error(err);
        if (err.status !== 404 && err.data) {
          handleError(err.data);
        }
        // it's fine to ignore error if status is 404
      } finally {
        this.setState({ isDocsLoading: false });
      }
    }
  };

  loadLivenessVideo = async () => {
    const { accountId, accountType } = this.props.data || {};

    // Liveness video only applies to mobile eKYC applicants.
    if (!accountId || accountType !== "MOBILE") return;

    // Release any previously fetched blob before re-fetching.
    if (this.state.liveness) {
      URL.revokeObjectURL(this.state.liveness);
    }

    this.setState({ livenessState: "loading", liveness: "" });

    try {
      const links = await verificationApi.getKycDownloadLinks(accountId);
      const url = links && links.liveNess;

      if (!url) {
        this.setState({ livenessState: "empty" });
        return;
      }

      // Download the video bytes so playback survives the pre-signed URL's
      // short (~10 min) expiry; play from a local object URL.
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);

      this.setState({ liveness: objectUrl, livenessState: "ready" });
    } catch (err) {
      console.error(err);
      this.setState({ livenessState: "error" });
    }
  };

  loadIdentityPhoto = async identityUuid => {
    // don't load photo if uuid is empty
    if (!identityUuid) return;

    const docs = await verificationApi.getDocumentsPhoto(identityUuid);
    this.setState({ docs });
  };

  loadUserPhoto = async photoUuid => {
    // don't load photo if uuid is empty
    if (!photoUuid) return;

    const photo = await verificationApi.getUserPhoto(photoUuid);
    this.setState({ photo });
  };

  loadUserHistory = async accountId => {
    if (!accountId) return;

    const updateHistory = await verificationApi.getUserHistoryByAccountId(
      accountId,
    );
    this.setState({ updateHistory });
  };

  loadUserVerificationHistory = async accountId => {
    if (!accountId) return;

    const identityHistory = await verificationApi.getUserIdentityHistory(
      accountId,
    );
    this.setState({ identityHistory });
  };

  isFrozen = () => this.props.data && this.props.data.frozen;

  onToggleFreezeAccount = () => {
    const isFrozen = this.isFrozen();
    swal({
      title: "Are you sure?",
      text: isFrozen
        ? "Do you want to unblock this account?"
        : "Do you want to block this account?",
      type: "warning",
      showCancelButton: true,
      confirmButtonText: isFrozen ? "Unblock" : "Block",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    }).then(async result => {
      if (result.value)
        try {
          if (isFrozen) {
            await usersApi.unfreezeAccount(this.props.data.accountId);
            swal("Success", "Account unblocked", "success");
          } else {
            await usersApi.freezeAccount(this.props.data.accountId);
            swal("Success", "Account blocked", "success");
          }
        } catch (error) {
          handleError(error);
        } finally {
          await this.props.updateAccount();
        }
    });
  };

  toggleDocsVisibility = () =>
    this.setState(({ isDocsVisible }) => ({ isDocsVisible: !isDocsVisible }));

  onChangeUserLimit = () => {
    const { data, updateAccount } = this.props;

    limitsApi
      .changeLimitForUser({
        accountId: this.props.data.id,
        limitId: this.state.selectedUserLimit,
      })
      .then(() =>
        swal(
          "Success",
          `Limit for user ${data.fullName} has been changed successfully`,
          "success",
        ).then(() => {
          updateAccount();
        }),
      )
      .catch(err => handleError(err));
  };

  onTogglePrepareRecoveryModal = () => {
    this.setState(({ isPrepareRecoveryOpen }) => ({
      isPrepareRecoveryOpen: !isPrepareRecoveryOpen,
    }));
  };

  onToggleKeyRecoveryModal = () => {
    this.setState(({ isKeyRecoveryOpen }) => ({
      isKeyRecoveryOpen: !isKeyRecoveryOpen,
    }));
  };

  onPrepareSuccess = () => {
    this.onTogglePrepareRecoveryModal();
    this.onToggleKeyRecoveryModal();
  };

  onToggleGenerateQrModal = () => {
    this.setState(({ isGenerateQrModalOpen }) => ({
      isGenerateQrModalOpen: !isGenerateQrModalOpen,
    }));
  };

  onToggleUpdateAccountModal = () => {
    const { updateAccount } = this.props;
    this.setState(({ isUpdateAccountModalOpen }) => {
      if (isUpdateAccountModalOpen) {
        updateAccount(this.props.data.verificationRequestId);
        this.loadUserHistory(this.props.data.accountId);
      }
      return {
        isUpdateAccountModalOpen: !isUpdateAccountModalOpen,
      };
    });
  };

  onToggleChangePhoneModal = () => {
    this.setState(({ isChangePhoneModalOpen }) => ({
      isChangePhoneModalOpen: !isChangePhoneModalOpen,
    }));
  };

  onToggleUpdateAdditionalInfoModal = () => {
    const { updateAccount } = this.props;
    this.setState(({ isUpdateAdditionalInfoModalOpen }) => {
      if (isUpdateAdditionalInfoModalOpen) {
        updateAccount(this.props.data.verificationRequestId);
        this.loadUserHistory(this.props.data.accountId);
      }
      return {
        isUpdateAdditionalInfoModalOpen: !isUpdateAdditionalInfoModalOpen,
      };
    });
  };

  displayPhotos = accountType => {
    if (this.state.isDocsLoading) {
      return <Spinner color="danger" />;
    }

    return (
      <Row>
        <Col sm="6">
          <Photo
            src={this.state.photo}
            name="User photo"
            data-cy="user-photo"
          />
        </Col>
        {accountType !== "DESKTOP" && (
          <Col sm="6">
            <Photo
              src={this.state.docs}
              name="Identity photo"
              data-cy="identity-photo"
            />
          </Col>
        )}
        {accountType === "MOBILE" && (
          <Col sm="6">
            <Video
              className="mt-4"
              src={this.state.liveness}
              state={this.state.livenessState}
              onRetry={this.loadLivenessVideo}
              name="Liveness Video"
              data-cy="liveness-video"
            />
          </Col>
        )}
      </Row>
    );
  };

  displayManageAuthorizersButton = ({ accountType, accountId }) => {
    if (accountType !== "DESKTOP") return null;

    return (
      <FormGroup row className="mb-4">
        <Col md="3">
          <Label>Authorizers:</Label>
        </Col>
        <Col xs="12" md="9">
          <Button
            color="secondary"
            onClick={() =>
              this.props.history.push(`/manage_authorizers/${accountId}`)
            }>
            Manage authorizers
          </Button>
        </Col>
      </FormGroup>
    );
  };

  deleteUserDialog = async id => {
    const assets = this.props.data.assets;
    const khrBalance = numberFormatter(assets["khr#nbc"], 0);
    const usdBalance = numberFormatter(assets["usd#nbc"], 2);
    const html = `
        <div class="delete-account-dialog">
          <div class="delete-account-info">
            You are about to delete Wallet ID <span>${id}</span> with a balance of <span>${khrBalance} KHR</span> and <span>${usdBalance} USD</span>. The user cannot be restored, recovered, or access their funds after deletion.
          </div>
          <p>
            Please type <span>Delete user ${id}</span> below to confirm
          </p>
          <input name="accountID" type="text" class="form-control" style="text-align: center" value="">
        </div>
      `;

    await swal({
      title: `Delete Account?`,
      html,
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
      showLoaderOnConfirm: true,
      width: 650,
      preConfirm: async () => {
        this.deleteUser(id);
      },
    });
  };

  deleteUser = async id => {
    const input = document.querySelector(".delete-account-dialog input");
    if (input.value !== `Delete user ${id}`) {
      swal.showValidationMessage(`Please type "Delete user ${id}" to confirm`);
      return;
    } else {
      usersApi
        .deleteUser(id)
        .then(() => {
          swal(
            "Success",
            `User ${id} has been deleted successfully`,
            "success",
          ).then(() => {
            window.location.reload();
          });
        })
        .catch(err => {
          handleError(err);
        });
    }
  };

  showRejectedReason = remarks => () => {
    swal("Reason", `${remarks || "No reason was provided."}`, "info");
  };

  renderCamDxResult = () => {
    const {
      kycCheckStatus,
      kycVerifyFirstName,
      kycVerifyLastName,
      kycVerifyDob,
      kycFaceMatching,
      kycRejectedReason,
      kycVerificationId,
      kycInitializeCheckDatetime,
      kycResultsDatetime,
    } = this.props.data || {};

    const hasResult = Boolean(
      kycCheckStatus ||
        kycVerificationId ||
        kycResultsDatetime ||
        kycInitializeCheckDatetime,
    );

    if (!hasResult) return null;

    const icon = ok => (
      <i
        className={ok ? "fa fa-check-circle" : "fa fa-times-circle"}
        style={{ color: ok ? "#28a745" : "#dc3545", marginRight: 6 }}
      />
    );

    const verifyRow = (label, value) => {
      let content = "";
      if (value === "CORRECT") {
        content = (
          <>
            {icon(true)}
            Correct
          </>
        );
      } else if (value === "INCORRECT") {
        content = (
          <>
            {icon(false)}
            Incorrect
          </>
        );
      } else if (value !== null && value !== undefined) {
        content = (
          <>
            {icon(false)}
            UNKNOWN
          </>
        );
      }
      return (
        <FormGroup row>
          <Col md="3">
            <Label>{label}:</Label>
          </Col>
          <Col xs="12" md="9">
            <Label>{content}</Label>
          </Col>
        </FormGroup>
      );
    };

    return (
      <Card data-cy="camdx-result">
        <CardHeader>CamDX Result</CardHeader>
        <CardBody>
          <Form className="form-horizontal">
            <FormGroup row>
              <Col md="3">
                <Label>KYC Status:</Label>
              </Col>
              <Col xs="12" md="9">
                <Label data-cy="camdx-kyc-status">{kycCheckStatus || ""}</Label>
              </Col>
            </FormGroup>
            {verifyRow("First Name", kycVerifyFirstName)}
            {verifyRow("Last Name", kycVerifyLastName)}
            {verifyRow("Date of Birth", kycVerifyDob)}
            <FormGroup row>
              <Col md="3">
                <Label>Face Matching:</Label>
              </Col>
              <Col xs="12" md="9">
                <Label data-cy="camdx-face-matching">
                  {typeof kycFaceMatching === "boolean" ? (
                    <>
                      {icon(kycFaceMatching)}
                      {kycFaceMatching ? "True" : "False"}
                    </>
                  ) : (
                    ""
                  )}
                </Label>
              </Col>
            </FormGroup>
            {kycCheckStatus === "FAILED" && kycRejectedReason && (
              <FormGroup row>
                <Col md="3">
                  <Label>Rejected Reason:</Label>
                </Col>
                <Col xs="12" md="9">
                  <Label style={{ overflowWrap: "anywhere" }}>
                    {String(kycRejectedReason)
                      .split(",")
                      .map(r => r.trim())
                      .filter(Boolean)
                      .join(", ")}
                  </Label>
                </Col>
              </FormGroup>
            )}
            <div className="border rounded p-3 mt-2 text-muted">
              <span className="mr-4">
                Verify ID:{" "}
                <span className="text-dark" data-cy="camdx-verify-id">
                  {kycVerificationId || ""}
                </span>
              </span>
              <span className="mr-4">
                Submitted At:{" "}
                <span className="text-dark">
                  {kycInitializeCheckDatetime
                    ? dateFormatter(kycInitializeCheckDatetime)
                    : ""}
                </span>
              </span>
              <span>
                Received At:{" "}
                <span className="text-dark">
                  {kycResultsDatetime ? dateFormatter(kycResultsDatetime) : ""}
                </span>
              </span>
            </div>
          </Form>
        </CardBody>
      </Card>
    );
  };

  render() {
    const {
      history,
      data: {
        accountType,
        fullName,
        organizationName,
        accountId,
        oldAccountId,
        primaryContact,
        secondaryContact,
        address,
        registeredDate,
        createdTime,
        requestedDate,
        birthday,
        sex,
        nationality,
        issuingCountry,
        documentId,
        documentExpiry,
        status,
        kycLimitId,
        kycStatus,
        additionalInfo,
      },
      type,
    } = this.props;

    const isAuditor = type === "auditor";

    const { updateHistory, identityHistory, limitsList } = this.state;
    const currentLimit = limitsList.find(l => l.id === kycLimitId);
    const viewFormatter = (cell, row) => {
      const { remarks } = row;
      return cell === "REJECTED" ? (
        <div>
          <span onClick={this.showRejectedReason(remarks)}>
            {cell}{" "}
            <i
              className="fa fa-info-circle"
              style={{ color: "#D15050", cursor: "pointer" }}
              data-cy="edit-limit"
            />
          </span>
        </div>
      ) : (
        <div>
          <span>{cell}</span>
        </div>
      );
    };

    const accountHistoryColumns = [
      {
        title: "Field",
        dataField: "fieldName",
        style: { width: "15%" },
      },
      {
        title: "Old value",
        dataFormat: (cell, row) => viewFormatter(cell, row),
        dataField: "oldValue",
      },
      {
        title: "New value",
        dataFormat: (cell, row) => viewFormatter(cell, row),
        dataField: "currentValue",
      },
      {
        title: "Changed by",
        dataField: "changedBy",
        style: { width: "20%" },
      },
      {
        title: "Date",
        dataField: "dateTime",
        style: { width: "20%" },
      },
    ];

    const identityHistoryColumns = [
      {
        title: "Live confidence %",
        dataField: "liveConfidenceAvg",
        style: { width: "15%" },
      },
      {
        title: "Liveness",
        dataField: "live",
      },
      {
        title: "Face match",
        dataField: "faceMatch",
      },
      {
        title: "Changed by",
        dataField: "changedBy",
        style: { width: "20%" },
      },
      {
        title: "Date",
        dataField: "dateTime",
        style: { width: "20%" },
      },
    ];

    return (
      <>
        <Card>
          <CardHeader>User Info</CardHeader>
          <CardBody>
            {!isAuditor && this.getStatusIndicator(status)}
            <Row className="justify-content-center">
              <Col>
                <Form className="form-horizontal">
                  {accountType === "MOBILE" && (
                    <FormGroup row>
                      <Col md="3">
                        <Label>Full Name:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label>{fullName}</Label>
                      </Col>
                    </FormGroup>
                  )}
                  <FormGroup row>
                    <Col md="3">
                      <Label>KYC status:</Label>
                    </Col>
                    <Col xs="12" md="9">
                      <Label>
                        {kycStatus}{" "}
                        {currentLimit && <>({currentLimit.limitName})</>}
                      </Label>
                    </Col>
                  </FormGroup>
                  {accountType !== "MOBILE" && (
                    <FormGroup row>
                      <Col md="3">
                        <Label>Organisation:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label>{organizationName}</Label>
                      </Col>
                    </FormGroup>
                  )}
                  <FormGroup row>
                    <Col md="3">
                      <Label>Account ID:</Label>
                    </Col>
                    <Col xs="12" md="9">
                      <Label>{accountId}</Label>
                    </Col>
                  </FormGroup>
                  {oldAccountId && (
                    <FormGroup row>
                      <Col md="3">
                        <Label>Source Account ID:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label data-cy="source-account-id">
                          {oldAccountId}
                        </Label>
                      </Col>
                    </FormGroup>
                  )}
                  <FormGroup row>
                    <Col md="3">
                      <Label>Status:</Label>
                    </Col>
                    <Col xs="12" md="9">
                      <Label>{status}</Label>
                    </Col>
                  </FormGroup>
                  {accountType === "MOBILE" && (
                    <FormGroup row>
                      <Col md="3">
                        <Label>Primary Contact:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label>{primaryContact}</Label>
                        {!isAuditor && (
                          <Button
                            disabled={status === "DELETED"}
                            style={{ marginLeft: "1rem" }}
                            color="warning"
                            onClick={this.onToggleChangePhoneModal}>
                            <span>Change primary contact</span>
                          </Button>
                        )}
                      </Col>
                    </FormGroup>
                  )}
                  <FormGroup row>
                    <Col md="3">
                      {accountType === "MOBILE" && (
                        <Label>Secondary Contact:</Label>
                      )}
                      {accountType !== "MOBILE" && (
                        <Label>Primary Contact:</Label>
                      )}
                    </Col>
                    <Col xs="12" md="9">
                      <Label>
                        {accountType === "MOBILE"
                          ? secondaryContact
                          : primaryContact}
                      </Label>
                    </Col>
                  </FormGroup>
                  <FormGroup row className="mb-4">
                    <Col md="3">
                      <Label>Address:</Label>
                    </Col>
                    <Col xs="12" md="9">
                      <Label>{address}</Label>
                    </Col>
                  </FormGroup>
                  {accountType === "MOBILE" && (
                    <FormGroup row>
                      <Col md="3">
                        <Label>Gender:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label>{sex || "unknown"}</Label>
                      </Col>
                    </FormGroup>
                  )}
                  {accountType === "MOBILE" && (
                    <FormGroup row>
                      <Col md="3">
                        <Label>Nationality:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label>{nationality || "unknown"}</Label>
                      </Col>
                    </FormGroup>
                  )}
                  {IS_TOURIST_PG && accountType === "MOBILE" && (
                    <FormGroup row>
                      <Col md="3">
                        <Label>Issuing Country:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label>{issuingCountry}</Label>
                      </Col>
                    </FormGroup>
                  )}
                  {accountType === "MOBILE" && (
                    <FormGroup row>
                      <Col md="3">
                        <Label>Document ID:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label>{documentId || "unknown"}</Label>
                      </Col>
                    </FormGroup>
                  )}
                  {accountType === "MOBILE" && (
                    <FormGroup row>
                      <Col md="3">
                        <Label>Birth date:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label>
                          {dateFormatter(birthday, {
                            showTime: false,
                            dateFormat: "array",
                          })}
                        </Label>
                      </Col>
                    </FormGroup>
                  )}
                  {accountType === "MOBILE" && (
                    <FormGroup row>
                      <Col md="3">
                        <Label>Document Expiry:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label>
                          {dateFormatter(documentExpiry, {
                            showTime: false,
                            dateFormat: "array",
                          })}
                          {isDocumentExpired(documentExpiry) && (
                            <small
                              className="text-danger ml-2"
                              style={{ fontWeight: 600 }}>
                              <i className="fa fa-exclamation-triangle mr-1" />
                              (Expired)
                            </small>
                          )}
                        </Label>
                      </Col>
                    </FormGroup>
                  )}
                  <FormGroup row className="mb-4">
                    <Col md="3">
                      <Label>Registration date:</Label>
                    </Col>
                    <Col xs="12" md="9">
                      <Label>
                        {dateFormatter(registeredDate || createdTime)}
                      </Label>
                    </Col>
                  </FormGroup>
                  {requestedDate && (
                    <FormGroup row className="mb-4">
                      <Col md="3">
                        <Label>Request Date:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Label>{dateFormatter(requestedDate)}</Label>
                      </Col>
                    </FormGroup>
                  )}
                  <FormGroup row className="mb-4">
                    <Col md="3">
                      <Label>Documents:</Label>
                    </Col>
                    <Col xs="12" md="9">
                      <Button
                        color="primary"
                        onClick={this.toggleDocsVisibility}
                        data-cy="show-photos">
                        {this.state.isDocsVisible ? "Hide" : "Show"}
                      </Button>
                      <Collapse isOpen={this.state.isDocsVisible}>
                        <Card style={{ marginTop: 5 }}>
                          <CardBody style={{ width: "100%" }}>
                            {this.displayPhotos(accountType)}
                          </CardBody>
                        </Card>
                      </Collapse>
                    </Col>
                  </FormGroup>
                  {IS_TOURIST_PG && !isAuditor && accountType === "MOBILE" && (
                    <FormGroup row className="mb-4">
                      <Col md="3">
                        <Label>GDI Verification:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Button
                          disabled={this.state.isGdiVerifying}
                          color="warning"
                          onClick={this.onVerifyWithGdi}>
                          {this.state.isGdiVerifying ? (
                            <>
                              <Spinner size="sm" className="mr-2" />
                              <span>Verifying...</span>
                            </>
                          ) : (
                            <span>Verify with GDI</span>
                          )}
                        </Button>
                        {this.renderGdiResult()}
                      </Col>
                    </FormGroup>
                  )}
                  {!isAuditor ? (
                    accountType === "MOBILE" ? (
                      <FormGroup row className="mb-4">
                        <Col md="3">
                          <Label>Prepare key recovery:</Label>
                        </Col>
                        <Col xs="12" md="9">
                          <Button
                            disabled={status === "DELETED"}
                            color="warning"
                            onClick={this.onTogglePrepareRecoveryModal}>
                            <i className="fa fa-key mr-2" />
                            <span>Prepare key recovery procedure</span>
                          </Button>
                        </Col>
                      </FormGroup>
                    ) : (
                      <FormGroup row className="mb-4">
                        <Col md="3">
                          <Label>Key recovery:</Label>
                        </Col>
                        <Col xs="12" md="9">
                          <Button
                            disabled={status === "DELETED"}
                            color="warning"
                            onClick={this.onToggleKeyRecoveryModal}>
                            <i className="fa fa-key mr-2" />
                            <span>Start key recovery procedure</span>
                          </Button>
                        </Col>
                      </FormGroup>
                    )
                  ) : (
                    ""
                  )}
                  {!isAuditor && (
                    <FormGroup row className="mb-4">
                      <Col md="3">
                        <Label>QR code:</Label>
                      </Col>
                      <Col xs="12" md="9">
                        <Button
                          disabled={status === "DELETED"}
                          color="warning"
                          onClick={this.onToggleGenerateQrModal}>
                          <span>Generate QR code</span>
                        </Button>
                      </Col>
                    </FormGroup>
                  )}
                  {!isAuditor && accountType === "MOBILE" && (
                    <FormGroup row className="mb-4">
                      <Col xs="12" md="9">
                        <Button
                          disabled={status === "DELETED"}
                          color="success"
                          onClick={this.onToggleUpdateAccountModal}>
                          <span>Open update account modal</span>
                        </Button>
                      </Col>
                    </FormGroup>
                  )}
                  {accountType === "MOBILE" && additionalInfo && (
                    <FormGroup row className="mb-4">
                      <Col xs="12" md="9">
                        <Button
                          disabled={status === "DELETED"}
                          color="success"
                          onClick={this.onToggleUpdateAdditionalInfoModal}>
                          <span>Update additional info</span>
                        </Button>
                      </Col>
                    </FormGroup>
                  )}
                  {!isAuditor && (
                    <FormGroup row className="mb-4">
                      <Col xs="12" md="9">
                        <Button
                          disabled={status === "DELETED"}
                          color="success"
                          onClick={this.onToggleFreezeAccount}>
                          <span>
                            {this.isFrozen()
                              ? "Unblock account"
                              : "Block account"}
                          </span>
                        </Button>
                      </Col>
                    </FormGroup>
                  )}

                  {!isAuditor &&
                    this.displayManageAuthorizersButton({
                      accountType,
                      accountId,
                    })}
                </Form>
                {accountType === "MOBILE" && !isAuditor && (
                  <Button
                    disabled={status === "DELETED"}
                    color="primary"
                    onClick={() => this.deleteUserDialog(accountId)}
                    className="float-left">
                    <i className="fa fa-user-times mr-2" />
                    Delete User
                  </Button>
                )}
                <Button
                  type="primary"
                  onClick={() => history.goBack()}
                  className="float-right"
                  data-cy="back">
                  <i className="fa fa-arrow-left mr-2" />
                  Back
                </Button>
              </Col>
            </Row>
          </CardBody>
          {!isAuditor && this.getFooterView()}
        </Card>

        {accountType === "MOBILE" && this.renderCamDxResult()}

        {!isAuditor && accountType === "MOBILE" && (
          <Card>
            <CardHeader>Profile history</CardHeader>
            <CardBody>
              <table className="table table-hover table-bordered table-sm dnd-table">
                <thead>
                  <tr className="dnd-table-header">
                    {accountHistoryColumns.map(column => (
                      <TableHeaderCell
                        key={column.title}
                        title={column.title}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {updateHistory.map((row, i) => (
                    <TableRow
                      key={i}
                      row={row}
                      columns={accountHistoryColumns}
                      isDragging={false}
                    />
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>
        )}

        {!isAuditor && accountType === "MOBILE" && (
          <Card>
            <CardHeader>Verification history</CardHeader>
            <CardBody>
              <table className="table table-hover table-bordered table-sm dnd-table">
                <thead>
                  <tr className="dnd-table-header">
                    {identityHistoryColumns.map(column => (
                      <TableHeaderCell
                        key={column.title}
                        title={column.title}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {identityHistory.map((row, i) => (
                    <TableRow
                      key={i}
                      row={row}
                      columns={identityHistoryColumns}
                      isDragging={false}
                    />
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>
        )}

        <PrepareRecoveryModal
          isOpen={this.state.isPrepareRecoveryOpen}
          onModalClose={this.onTogglePrepareRecoveryModal}
          onSuccess={this.onPrepareSuccess}
          userData={{ accountId, fullName }}
        />

        <KeyRecoveryModal
          isOpen={this.state.isKeyRecoveryOpen}
          isKeyRecovery={false}
          onModalClose={this.onToggleKeyRecoveryModal}
          userData={{ accountId, fullName, documentId }}
          organization={accountType === "DESKTOP" ? organizationName : null}
        />

        <GenerateQrModal
          isOpen={this.state.isGenerateQrModalOpen}
          onModalClose={this.onToggleGenerateQrModal}
          userAccountId={accountId}
          organizationName={organizationName}
        />

        <UpdateAccountModal
          isOpen={this.state.isUpdateAccountModalOpen}
          onModalClose={this.onToggleUpdateAccountModal}
          // Saved passport data changed; the admin must verify with GDI again.
          onUpdated={() => this.setState({ gdiResult: null })}
          data={this.props.data}
        />

        <ChangePhoneModal
          isOpen={this.state.isChangePhoneModalOpen}
          onModalClose={this.onToggleChangePhoneModal}
          userAccountId={accountId}
        />

        {accountType === "MOBILE" && additionalInfo && (
          <UpdateAdditionalInfoModal
            isOpen={this.state.isUpdateAdditionalInfoModalOpen}
            onModalClose={this.onToggleUpdateAdditionalInfoModal}
            data={this.props.data.additionalInfo}
            userAccountId={accountId}
          />
        )}
      </>
    );
  }
}

export default withRouter(CustomerInfoCom);
